// KiraModal.jsx — floating chat window for K.I.R.A
// Uses the floater-specific Friendly Bot avatar (sibling file)
// and the existing InlineActionForm for action UX.
import React, { useEffect, useRef, useState } from "react";
import { Send, X, Minus, History } from "lucide-react";
import KiraAvatar       from "./KiraAvatar";
import KiraActionModals, { isModalAction } from "./KiraActionModals";
import InlineActionForm from "../chat/InlineActionForm";
import ActionCard       from "./ActionCard";
import ActionList       from "./ActionList";

const NEON         = "#00FF99";
const W            = 520;
const H            = 620;
const MARGIN       = 16;
const IDLE_MS      = 60_000;
const WARN_MS      = 50_000;
const DRAG_THRESHOLD = 5;

function clampPos(p) {
  const maxLeft = Math.max(MARGIN, window.innerWidth  - W - MARGIN);
  const maxTop  = Math.max(MARGIN, window.innerHeight - H - MARGIN);
  return {
    left: Math.min(Math.max(MARGIN, p.left), maxLeft),
    top:  Math.min(Math.max(MARGIN, p.top),  maxTop),
  };
}

// Tiny passthrough kept for parity with the previous wrapper. The new
// KiraAvatar accepts `size` as a number directly so no scale hack is needed.
function MiniAvatar({ emotion, px = 36, isTyping = false }) {
  return <KiraAvatar emotion={emotion} size={px} isTyping={isTyping} />;
}

// ─── Light markdown renderer: **bold**, bullet lines (- or •), line breaks ───
function renderRichText(text) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return lines.map((line, i) => {
    const trimmed = line.trimStart();
    const isBullet = /^[-•]\s+/.test(trimmed);
    const body = isBullet ? trimmed.replace(/^[-•]\s+/, "") : line;
    const parts = body.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((seg, j) => {
      if (/^\*\*[^*]+\*\*$/.test(seg)) {
        return <strong key={j} style={{ color: "#fff", fontWeight: 600 }}>{seg.slice(2, -2)}</strong>;
      }
      return <span key={j}>{seg}</span>;
    });
    if (isBullet) {
      return (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ color: NEON, lineHeight: 1.5 }}>•</span>
          <span style={{ flex: 1 }}>{parts}</span>
        </div>
      );
    }
    return <div key={i}>{parts.length ? parts : <br/>}</div>;
  });
}

function fmtTime(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function KiraModal({
  isOpen,
  onClose,
  messages = [],
  emotion = "neutral",
  isLoading = false,
  isTyping = false,
  onSendMessage,
  onTyping,
  pendingAction,
  onConfirmAction,
  onCancelAction,
  position,
  onPositionChange,
  // Conversational action props (new flow)
  actionCard,
  actionList,
  isExecuting,
  onSelectActionItem,
  onConfirmActionCard,
  onCancelActionCard,
  // Thread persistence (server-side history)
  createThread,
  setMessages,
  loadThreadMessages,
  setThreadId,
}) {
  const [draft, setDraft]             = useState("");
  const [drag, setDrag]               = useState(false);
  const [warnIdle, setWarnIdle]       = useState(false);
  const [mounted, setMounted]         = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [threads, setThreads]         = useState([]);

  const loadThreads = async () => {
    const token = localStorage.getItem("token");
    const API   = process.env.REACT_APP_API_BASE || "http://localhost:8000";
    try {
      const res  = await fetch(`${API}/api/kira/threads`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      setThreads(Array.isArray(data) ? data : []);
    } catch (e) {
      setThreads([]);
    }
  };

  useEffect(() => {
    if (showThreads) loadThreads();
  }, [showThreads]);

  const dragRef    = useRef({ ox: 0, oy: 0, moved: 0, startX: 0, startY: 0 });
  const idleRef    = useRef({ closeT: null, warnT: null });
  const modalRef   = useRef(null);
  const messagesEnd = useRef(null);
  const inputRef   = useRef(null);

  // mount animation
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(t);
    }
    setMounted(false);
  }, [isOpen]);

  // re-clamp on resize
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => onPositionChange?.(clampPos(position));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen, position, onPositionChange]);

  // auto-scroll on new message
  useEffect(() => {
    if (!isOpen) return;
    messagesEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading, isOpen, pendingAction]);

  // ── Auto-focus the input on open + after AI/action completes ──
  // 100ms delay on open lets the mount/animation settle before focus.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Re-focus when an in-flight reply or action card finishes.
  // Combined into one effect so a single state transition (isLoading → false
  // OR isExecuting → false) returns the cursor to the input.
  useEffect(() => {
    if (!isOpen) return;
    if (isLoading || isExecuting) return;
    // Small delay covers the case where ActionCard re-renders into its
    // "Submitted" state in the same tick.
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isLoading, isExecuting, isOpen]);

  // ── Idle close (60s) + warn (50s)
  const resetIdle = () => {
    setWarnIdle(false);
    if (idleRef.current.warnT)  clearTimeout(idleRef.current.warnT);
    if (idleRef.current.closeT) clearTimeout(idleRef.current.closeT);
    idleRef.current.warnT  = setTimeout(() => setWarnIdle(true), WARN_MS);
    idleRef.current.closeT = setTimeout(() => onClose?.(),       IDLE_MS);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetIdle();
    return () => {
      if (idleRef.current.warnT)  clearTimeout(idleRef.current.warnT);
      if (idleRef.current.closeT) clearTimeout(idleRef.current.closeT);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Click outside closes
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e) => {
      const m = modalRef.current;
      if (m && m.contains(e.target)) return;
      // ignore clicks on the float bubble itself
      const inFloat = e.target.closest && e.target.closest('[aria-label="Open K.I.R.A chat"]');
      if (inFloat) return;
      const inModal = e.target.closest?.('[data-kira-modal]');
      if (inModal) return;
      onClose?.();
    };
    // small delay so the same click that opened the modal doesn't immediately close it
    const t = setTimeout(() => document.addEventListener("mousedown", onMouseDown), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isOpen, onClose]);

  // ── Header drag
  const onHeaderMouseDown = (e) => {
    if (e.target.closest("button")) return;        // ignore close/minimise
    e.preventDefault();
    dragRef.current = {
      ox:     e.clientX - position.left,
      oy:     e.clientY - position.top,
      moved:  0,
      startX: e.clientX,
      startY: e.clientY,
    };
    setDrag(true);

    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      dragRef.current.moved = Math.max(dragRef.current.moved, Math.hypot(dx, dy));
      onPositionChange?.(clampPos({
        left: ev.clientX - dragRef.current.ox,
        top:  ev.clientY - dragRef.current.oy,
      }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      setDrag(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  const submit = () => {
    const t = draft.trim();
    if (!t || isLoading) return;
    onSendMessage?.(t);
    setDraft("");
    resetIdle();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (!isOpen) return null;

  const showEmpty = messages.length === 0 && !isLoading;

  return (
    <>
      <div
        ref={modalRef}
        onMouseMove={resetIdle}
        onKeyDownCapture={resetIdle}
        style={{
          position:      "fixed",
          left:          position.left,
          top:           position.top,
          width:         W,
          height:        H,
          zIndex:        9998,
          background:    "#0d0d0d",
          border:        "1px solid rgba(0,255,153,0.15)",
          borderRadius:  16,
          boxShadow:     "0 8px 40px rgba(0,0,0,0.8), 0 0 30px rgba(0,255,153,0.06)",
          display:       "flex",
          flexDirection: "column",
          overflow:      "hidden",
          opacity:       mounted ? 1 : 0,
          transform:     mounted ? "translateY(0)" : "translateY(8px)",
          transition:    "opacity 200ms ease, transform 200ms ease",
        }}
      >
        {/* HEADER — draggable */}
        <div
          onMouseDown={onHeaderMouseDown}
          style={{
            height:          56,
            background:      "#111111",
            borderBottom:    "1px solid rgba(255,255,255,0.06)",
            borderRadius:    "16px 16px 0 0",
            cursor:          drag ? "grabbing" : "grab",
            display:         "flex",
            alignItems:      "center",
            justifyContent:  "space-between",
            padding:         "0 14px",
            userSelect:      "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MiniAvatar emotion={emotion} px={36} isTyping={isTyping} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 600, letterSpacing: 0.2 }}>K.I.R.A</span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>AI Assistant</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => setShowThreads(prev => !prev)}
              title="Conversation history"
              style={{
                background:     "none",
                border:         "none",
                color:          "rgba(255,255,255,0.5)",
                cursor:         "pointer",
                padding:        4,
                borderRadius:   4,
                display:        "flex",
                alignItems:     "center",
              }}
            >
              <History size={16} />
            </button>
            <HeaderBtn icon={<Minus size={14}/>} onClick={onClose} title="Minimise" />
            <HeaderBtn icon={<X size={14}/>}     onClick={onClose} title="Close" />
          </div>
        </div>

        {/* IDLE WARNING */}
        {warnIdle && (
          <div style={{
            background:  "rgba(255,153,0,0.10)",
            borderBottom:"1px solid rgba(255,153,0,0.30)",
            color:       "#ffb84d",
            padding:     "8px 16px",
            fontSize:    12,
          }}>
            K.I.R.A will close in 10 seconds.
          </div>
        )}

        {/* MESSAGES (or THREADS panel) */}
        {showThreads ? (
          <div style={{
            flex:        1,
            overflowY:   "auto",
            padding:     12,
            background:  "#111111",
          }}>
            {/* New conversation button */}
            <button
              onClick={() => {
                createThread?.();
                setMessages?.([]);
                setShowThreads(false);
              }}
              style={{
                width:        "100%",
                padding:      10,
                background:   "rgba(0,255,153,0.1)",
                border:       "1px solid rgba(0,255,153,0.3)",
                borderRadius: 8,
                color:        "#00FF99",
                cursor:       "pointer",
                marginBottom: 12,
                fontSize:     13,
                fontWeight:   500,
              }}
            >
              + New Conversation
            </button>

            {/* Thread list */}
            {threads.map(thread => (
              <div
                key={thread.id}
                onClick={() => {
                  loadThreadMessages?.(thread.id);
                  setThreadId?.(thread.id);
                  setShowThreads(false);
                }}
                style={{
                  padding:      "10px 12px",
                  borderRadius: 8,
                  marginBottom: 6,
                  background:   "rgba(255,255,255,0.03)",
                  border:       "1px solid rgba(255,255,255,0.06)",
                  cursor:       "pointer",
                  position:     "relative",
                }}
              >
                {thread.is_pinned && (
                  <span style={{
                    fontSize:    10,
                    color:       "#00FF99",
                    marginRight: 6,
                  }}>📌</span>
                )}
                <div style={{
                  fontSize:     13,
                  color:        "#fff",
                  fontWeight:   500,
                  marginBottom: 2,
                  paddingRight: 24,
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace:   "nowrap",
                }}>
                  {thread.title}
                </div>
                <div style={{
                  fontSize: 11,
                  color:    "rgba(255,255,255,0.4)",
                }}>
                  {thread.last_message_preview || ""}
                </div>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm("Delete this conversation?")) return;
                    const token = localStorage.getItem("token");
                    const API   = process.env.REACT_APP_API_BASE || "http://localhost:8000";
                    try {
                      const res = await fetch(`${API}/api/kira/threads/${thread.id}`, {
                        method: "DELETE",
                        headers: { "Authorization": `Bearer ${token}` },
                      });
                      if (res.ok) {
                        setThreads(prev => prev.filter(t => t.id !== thread.id));
                      }
                    } catch (_) {
                      // silent fail
                    }
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#EF4444"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.2)"; }}
                  title="Delete conversation"
                  style={{
                    position:     "absolute",
                    top:          8,
                    right:        8,
                    background:   "none",
                    border:       "none",
                    color:        "rgba(255,255,255,0.2)",
                    cursor:       "pointer",
                    padding:      4,
                    fontSize:     14,
                    lineHeight:   1,
                    borderRadius: 4,
                    flexShrink:   0,
                  }}
                >
                  🗑
                </button>
              </div>
            ))}

            {threads.length === 0 && (
              <div style={{
                textAlign: "center",
                color:     "rgba(255,255,255,0.3)",
                fontSize:  13,
                marginTop: 40,
              }}>
                No previous conversations
              </div>
            )}
          </div>
        ) : (
        <div
          onMouseMove={resetIdle}
          style={{
            flex:        1,
            overflowY:   "auto",
            padding:     16,
            background:  "#111111",
            display:     "flex",
            flexDirection:"column",
            gap:         10,
          }}
        >
          {showEmpty && (
            <div style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: 14, color: "rgba(255,255,255,0.7)",
            }}>
              <MiniAvatar emotion="neutral" px={64} />
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  Hi! I'm K.I.R.A
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>
                  Your AI HR assistant. How can I help?
                </div>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageRow
              key={m.id || i}
              m={m}
              isExecuting={isExecuting}
              onConfirmActionCard={onConfirmActionCard}
              onCancelActionCard={onCancelActionCard}
            />
          ))}

          {isLoading && <TypingBubble />}
          <div ref={messagesEnd} />
        </div>
        )}

        {/* CONVERSATIONAL ACTION LIST (selection step) — cards now render
            inline within MessageRow so they persist as history. */}
        {actionList && !actionCard && (
          <div style={{
            borderTop:   "1px solid rgba(0,255,153,0.10)",
            padding:     "8px 16px",
            maxHeight:   360,
            overflowY:   "auto",
          }}>
            <ActionList
              list      = {actionList}
              isPending = {!!isExecuting}
              onSelect  = {onSelectActionItem}
            />
          </div>
        )}

        {/* LEGACY PENDING ACTION FORM — kept for non-conversational actions (modal-class actions render below as overlays) */}
        {pendingAction && !isModalAction(pendingAction.type) && (
          <div style={{
            background:  "rgba(0,255,153,0.04)",
            borderTop:   "1px solid rgba(0,255,153,0.15)",
            padding:     "12px 16px",
            maxHeight:   320,
            overflowY:   "auto",
          }}>
            <InlineActionForm
              action     = {pendingAction.type}
              details    = {pendingAction.details || {}}
              onSuccess  = {(msg) => onConfirmAction?.(msg)}
              onDismiss  = {onCancelAction}
            />
          </div>
        )}

        {/* INPUT BAR */}
        <div style={{
          height:        64,
          background:    "#111111",
          borderTop:     "1px solid rgba(255,255,255,0.06)",
          borderRadius:  "0 0 16px 16px",
          padding:       "12px 16px",
          display:       "flex",
          gap:           8,
          alignItems:    "center",
        }}>
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); resetIdle(); onTyping?.(); }}
            onKeyDown={onKeyDown}
            placeholder="Ask K.I.R.A anything..."
            disabled={isLoading}
            style={{
              flex:        1,
              background:  "#0d0d0d",
              border:      "1px solid rgba(255,255,255,0.10)",
              borderRadius: 8,
              color:       "#fff",
              fontSize:    14,
              padding:     "8px 12px",
              resize:      "none",
              outline:     "none",
              fontFamily:  "inherit",
              lineHeight:  1.4,
              maxHeight:   80,
              transition:  "border-color 0.15s",
            }}
            onFocus={(e) => e.target.style.borderColor = "rgba(0,255,153,0.40)"}
            onBlur={(e)  => e.target.style.borderColor = "rgba(255,255,255,0.10)"}
          />
          <button
            onClick={submit}
            disabled={!draft.trim() || isLoading}
            style={{
              width:        40,
              height:       40,
              borderRadius: 8,
              border:       "none",
              background:   (!draft.trim() || isLoading) ? "rgba(255,255,255,0.10)" : NEON,
              color:        (!draft.trim() || isLoading) ? "rgba(255,255,255,0.30)" : "#000",
              cursor:       (!draft.trim() || isLoading) ? "not-allowed" : "pointer",
              display:      "grid",
              placeItems:   "center",
              transition:   "background 0.15s",
              flexShrink:   0,
            }}
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Modal-class actions render as full overlays outside the chat box */}
      {pendingAction && isModalAction(pendingAction.type) && (
        <KiraActionModals
          activeModal = {pendingAction.type}
          modalData   = {pendingAction.details || {}}
          onClose     = {onCancelAction}
          onSuccess   = {onConfirmAction}
        />
      )}

      <style>{`
        @keyframes kiraDot {
          0%, 80%, 100% { transform: translateY(0);   opacity: 0.4; }
          40%           { transform: translateY(-6px); opacity: 1;   }
        }
      `}</style>
    </>
  );
}

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

function HeaderBtn({ icon, onClick, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title}
      aria-label={title}
      style={{
        width:        28,
        height:       28,
        borderRadius: 6,
        border:       "1px solid rgba(255,255,255,0.06)",
        background:   hover ? "rgba(255,255,255,0.06)" : "transparent",
        color:        "rgba(255,255,255,0.7)",
        cursor:       "pointer",
        display:      "grid",
        placeItems:   "center",
        transition:   "background 0.15s, color 0.15s",
      }}
    >
      {icon}
    </button>
  );
}

function MessageRow({ m, isExecuting, onConfirmActionCard, onCancelActionCard }) {
  const isUser = m.role === "user" || m.sender === "user";
  if (isUser) {
    return (
      <div style={{ alignSelf: "flex-end", maxWidth: "80%" }}>
        <div style={{
          background:  "#1a1a1a",
          border:      "1px solid rgba(255,255,255,0.08)",
          borderRadius:"12px 12px 2px 12px",
          padding:     "10px 14px",
          color:       "#fff",
          fontSize:    14,
          lineHeight:  1.45,
          height:      "auto",
          overflow:    "visible",
          whiteSpace:  "pre-wrap",
          wordWrap:    "break-word",
          wordBreak:   "break-word",
          overflowWrap:"anywhere",
        }}>
          {m.content || m.text}
        </div>
        <div style={{
          color: "rgba(255,255,255,0.30)", fontSize: 11,
          textAlign: "right", marginTop: 3, paddingRight: 4,
        }}>
          {fmtTime(m.timestamp || m.ts)}
        </div>
      </div>
    );
  }
  return (
    <div style={{ alignSelf: "flex-start", maxWidth: "80%" }}>
      <div style={{ color: NEON, fontSize: 10, fontWeight: 600, marginBottom: 3, paddingLeft: 4, letterSpacing: 0.5 }}>
        K.I.R.A
      </div>
      <div style={{
        background:    "#0a0a0a",
        border:        "1px solid rgba(0,255,153,0.10)",
        borderLeft:    "3px solid #00FF99",
        borderRadius:  "0 12px 12px 12px",
        padding:       "10px 14px",
        color:         "#fff",
        fontSize:      14,
        lineHeight:    1.5,
        height:        "auto",
        overflow:      "visible",
        whiteSpace:    "pre-wrap",
        wordWrap:      "break-word",
        wordBreak:     "break-word",
        overflowWrap:  "anywhere",
      }}>
        {renderRichText(m.content || m.text)}
      </div>
      {/* Inline ActionCard — appears alongside the AI message that introduced it.
          Stays in history in "Submitted" state once the user confirms. */}
      {m.action_card && (
        <ActionCard
          card        = {m.action_card}
          isExecuting = {!m.action_card_executed && isExecuting}
          executed    = {!!m.action_card_executed}
          executedAt  = {m.action_card_executed_at || null}
          onConfirm   = {onConfirmActionCard}
          onCancel    = {onCancelActionCard}
        />
      )}
      <div style={{
        color: "rgba(255,255,255,0.30)", fontSize: 11,
        textAlign: "left", marginTop: 3, paddingLeft: 4,
      }}>
        {fmtTime(m.timestamp || m.ts)}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div style={{ alignSelf: "flex-start", maxWidth: "80%" }}>
      <div style={{ color: NEON, fontSize: 10, fontWeight: 600, marginBottom: 3, paddingLeft: 4, letterSpacing: 0.5 }}>
        K.I.R.A
      </div>
      <div style={{
        background:    "#0a0a0a",
        border:        "1px solid rgba(0,255,153,0.10)",
        borderLeft:    "3px solid #00FF99",
        borderRadius:  "0 12px 12px 12px",
        padding:       "10px 14px",
        display:       "flex",
        gap:           4,
        alignItems:    "center",
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width:       6, height: 6, borderRadius: "50%",
            background:  NEON,
            display:     "inline-block",
            animation:   `kiraDot 1.2s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
          }}/>
        ))}
      </div>
    </div>
  );
}
